import React, { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { CreditCard, ShieldCheck, Zap, Tag } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { STATUS_LABELS, STATUS_COLORS, REASON_LABEL } from "../components/admin-orders/constants";
import { OrderProjectInfo } from "../components/admin-orders/OrderProjectInfo";
import { OrderActions } from "../components/admin-orders/OrderActions";
import { OrderDetails } from "../components/admin-orders/OrderDetails";

function OrderRow({ o, onRefresh, onDeleted }) {
  const [showDetails, setShowDetails] = useState(false);
  const cardOnFile = !!o.card_on_file;

  return (
    <div data-testid={`admin-row-${o.order_id}`} className="rounded-lg border hairline bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-xs text-[color:var(--text-muted)]">{o.order_id}</div>
          <div className="font-serif text-xl mt-0.5" style={{color: "var(--primary)"}}>{o.customer_name}</div>
          <div className="text-xs text-[color:var(--text-muted)]">{o.email} · {o.phone}</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Badge className={STATUS_COLORS[o.status] || "bg-neutral-200"}>{STATUS_LABELS[o.status] || o.status}</Badge>
          {o.rush && <Badge className="bg-amber-200 text-amber-900"><Zap size={10} className="mr-1"/>Rush</Badge>}
          {o.military_discount && (
            <Badge className={o.military_proof_submitted ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-900"}>
              <ShieldCheck size={10} className="mr-1"/>Military{o.military_proof_submitted ? " · proof" : " · pending"}
            </Badge>
          )}
          {o.discount_code_applied && <Badge className="bg-amber-100 text-amber-900">Code · {o.discount_code_applied}</Badge>}
          {cardOnFile && <Badge className="bg-emerald-100 text-emerald-900"><CreditCard size={10} className="mr-1"/>Card on file</Badge>}
        </div>
      </div>

      {o.customer_response && (
        <div data-testid={`customer-response-${o.order_id}`} className="mb-3 rounded border hairline p-3 text-xs" style={{background: "rgba(197,160,89,0.08)"}}>
          <b>Customer:</b> {o.customer_response.action === "approve" ? "approved the quote" : o.customer_response.action === "cancel" ? "cancelled the order" : "requested an alternative"}
          {o.customer_response.reason && <> — <i>{REASON_LABEL[o.customer_response.reason] || o.customer_response.reason}</i></>}
          {o.customer_response.note && <div className="mt-1 text-[color:var(--text-muted)]">"{o.customer_response.note}"</div>}
        </div>
      )}

      {o.design_change_log?.length > 0 && (
        <div data-testid={`design-changes-${o.order_id}`} className="mb-3 rounded border hairline p-3 text-xs bg-white">
          <b>Design changes:</b>
          <ul className="mt-1 space-y-1">
            {o.design_change_log.slice(-3).reverse().map((d) => (
              <li key={d.at} className="text-[color:var(--text-muted)]">{new Date(d.at).toLocaleString()} — {d.description.slice(0, 160)}{d.description.length > 160 ? "…" : ""}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        <OrderProjectInfo o={o}/>
        <OrderActions o={o} onRefresh={onRefresh} onDeleted={onDeleted} showDetails={showDetails} onToggleDetails={() => setShowDetails((s) => !s)}/>
      </div>

      {showDetails && <OrderDetails o={o}/>}
    </div>
  );
}

export default function AdminOrders() {
  const { user, loading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
    } catch (e) {
      if (e.response?.status === 403) toast.error("Admin only");
      else { console.error("Load failed", e); toast.error("Could not load — please refresh"); }
    }
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  if (loading) return <div className="p-10 text-center text-[color:var(--text-muted)]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace/>;
  if (!user.is_admin && !(user.email || "").toLowerCase().includes("admin")) {
    // allow seeded test admin by email too; backend still enforces authoritative check
  }

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  return (
    <div data-testid="admin-orders-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="uppercase text-xs tracking-[0.3em]" style={{color: "var(--accent-metal)"}}>Studio Console</div>
          <h1 className="font-serif text-4xl mt-2" style={{color: "var(--primary)"}}>Orders & Quotes</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/inbox"><Button variant="outline" size="sm" className="rounded-full">View inbox →</Button></Link>
          <Link to="/admin/discount-codes"><Button variant="outline" size="sm" className="rounded-full"><Tag size={12} className="mr-1"/>Discount codes</Button></Link>
          <Button data-testid="admin-refresh" variant="outline" onClick={load} className="rounded-full">Refresh</Button>
        </div>
      </div>

      <div data-testid="admin-filters" className="flex gap-2 flex-wrap mb-6">
        {[["all", "All"], ["pending_quote", "Awaiting Quote"], ["quoted", "Quote Sent"], ["pending_payment", "Awaiting Payment"], ["in_production", "In Production"], ["shipped", "Shipped"], ["delivered", "Delivered"]].map(([k, lbl]) => (
          <Button
            key={k}
            data-testid={`filter-${k}`}
            onClick={() => setFilter(k)}
            variant={filter === k ? "default" : "outline"}
            size="sm"
            className={`rounded-full text-xs ${filter === k ? "btn-primary" : ""}`}
          >
            {lbl} {k === filter ? `· ${filtered.length}` : ""}
          </Button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div data-testid="admin-empty" className="text-center p-10 border hairline rounded-lg bg-white text-[color:var(--text-muted)]">
          No orders in this view.
        </div>
      ) : (
        <div className="space-y-4" data-testid="admin-order-list">
          {filtered.map((o) => <OrderRow key={o.order_id} o={o} onRefresh={load} onDeleted={() => setOrders((xs) => xs.filter((x) => x.order_id !== o.order_id))}/>)}
        </div>
      )}
    </div>
  );
}
